import React from 'react';
import styled, { css } from 'styled-components/macro';
import TransitionGroup from 'react-transition-group/TransitionGroup';
import CSSTransition from 'react-transition-group/CSSTransition';

import {
	Box,
	Text,
	Tile,
	Flex,
	PrimaryButton,
	Hide,
	Link,
	SmartLink,
	Tooltip,
	Layout,
	Select,
	TertiaryButton,
	RecurringIcon,
} from '@stashinvest/ui';

import { SignUpStep } from 'stash/components/sign-up/SignUpScreenLayout';
import { connect } from 'react-redux';
import { openDialog } from '../../actions/dialog';
import { selectAccount } from 'stash/selectors/account';
import { trackEvent } from 'stash/actions';
import { accountTypes } from 'stash/constants/accountTypes';

import {
	setSignUpData,
	setSignUpAutostash,
	setSkipAutostash,
	setSignUpAccountsForAutostash,
} from 'stash/actions/signUp';

import { postBuyWithDeposit, postAccountAutoStash } from 'stash/actions/api';

import { ALL_AUTO_STASH_FREQUENCY_OPTIONS } from '../../constants/mappings';
import { ROTH_IRA, TRADITIONAL_IRA } from 'stash/constants/accountTypes';
import { usdCurrency } from 'stash/utils/formatters';
import { getTomorrow } from 'stash/utils/time';
import * as validations from 'stash/utils/formValidations';
import { format } from 'date-fns';

const DropdownContainer = styled(Box)`
	width: '100%';
	max-width: '100%';
	padding: 0;
`;

const StyledTile = styled(Tile)`
	min-height: 24rem;
`;

const TileEmphasisText = (props) => (
	<Text as="span" fontWeight="bold" color="textPrimary" {...props} />
);

const amounts = [
	{
		value: 10,
		label: '$10',
	},
	{
		value: 20,
		label: '$20',
	},
	{
		value: 50,
		label: '$50',
	},
	{
		value: 100,
		label: '$100',
	},
	{
		value: 200,
		label: '$200',
	},
];

const quantifiedFrequency = {
	NONE: 1,
	DAILY: 260,
	WEEKLY: 52,
	'BI-WEEKLY': 26,
	MONTHLY: 12,
};

const frequencies = ALL_AUTO_STASH_FREQUENCY_OPTIONS.map((option) => {
	return {
		value: option.value,
		label: option.short_title,
	};
});

const mapStateToProps = (state, ownProps) => {
	const firstName = state.entities.user.first_name;
	const isRetireAddOn = ownProps.product === 'retire-add-on';
	const investAccount = selectAccount(state, 'PERSONAL_BROKERAGE');
	const retireAccount =
		selectAccount(state, 'ROTH_IRA') || selectAccount(state, 'TRADITIONAL_IRA');
	const accounts = Object.values(state.entities.account);
	const bankAccount = Object.values(state.entities.bank_account);

	const hasInvestEsig = !!(investAccount && investAccount.e_signature_agreed_at);
	const hasRetireEsig = !!(retireAccount && retireAccount.e_signature_agreed_at);
	const retireAccountType = state.application.signUp.data.retireAccountType;

	return {
		accounts,
		bankAccount,
		isRetireAddOn,
		firstName,
		investAccount,
		retireAccount,
		hasInvestEsig,
		hasRetireEsig,
		retireAccountType,
	};
};

const mapDispatchToProps = (dispatch, ownProps) => ({
	resetStep: () => {
		dispatch(
			setSignUpData({
				auto_stash: {},
			})
		);
	},
	onSubmit: async (amount, frequency, accounts, bankAccount, isRetireAddOn) => {
		const auto_stash = {
			amount,
			frequency,
			paused: false,
			next_execution: format(getTomorrow(), 'yyyy-MM-dd'),
			allocations: [{ card_id: -1, amount }],
		};
		const accountsForAutostash = accounts.map((account) => account.id).reverse();
		const retireAccount = accounts.filter(
			(account) => account.type === ROTH_IRA || account.type === TRADITIONAL_IRA
		);

		if (!isRetireAddOn) {
			dispatch(setSignUpAccountsForAutostash(accountsForAutostash));
		}

		if (isRetireAddOn && bankAccount.length) {
			try {
				await Promise.all([
					dispatch(postAccountAutoStash(retireAccount[0].id, { auto_stash })),
					dispatch(postBuyWithDeposit(retireAccount[0].id, -1, amount)),
				]);
			} catch (error) {
				console.error(error);
				await dispatch(
					openDialog({
						prompt: 'Something went wrong. Please try again.',
						confirmText: 'Try again',
					})
				);
				window.location.reload();
			}
		}

		dispatch(setSignUpAutostash({ auto_stash }));

		dispatch(
			trackEvent('FundYourAccount', {
				ScreenName: 'SavingsPlan',
				Action: 'ConfirmPlan',
				Schedule: frequency,
				Amount: amount,
			})
		);

		ownProps.completeStep();
	},
	track: (eventName, properties) => {
		dispatch(trackEvent(eventName, properties));
	},
	skip: () => {
		dispatch(
			openDialog({
				heading: 'Are you sure?',
				prompt:
					'Adding a little bit on a regular basis is a smart investing strategy. You can always change it or turn it off.',
				confirmText: 'Continue',
				cancelText: 'Go back',
			})
		)
			.then(() => {
				dispatch(setSkipAutostash(true));
				ownProps.completeStep();
			})
			.catch(() => {});
	},
});

class SignUpAutoStash extends React.Component {
	constructor(props) {
		super(props);

		this.renderSavingsPlan = this.renderSavingsPlan.bind(this);
		this.buyMax = validations.buyMax(10000);

		this.state = {
			amount: amounts[0],
			frequency: frequencies[0],
			editing: false,
		};
	}

	componentDidMount() {
		const { hasInvestEsig, hasRetireEsig } = this.props;

		this.props.resetStep();

		this.props.track('FundYourAccount', {
			ScreenName: 'SavingsPlan',
			HasInvestAccount: hasInvestEsig,
			HasRetireAccount: hasRetireEsig,
		});
	}

	computeSavings(amount, frequency) {
		return usdCurrency(amount * quantifiedFrequency[frequency]);
	}

	setEditing(editing) {
		const { amount, frequency } = this.state;

		if (editing) {
			this.props.track('FundYourAccount', {
				ScreenName: 'SavingsPlan',
				Action: 'EditPlan',
				Amount: amount.value,
				Schedule: frequency.value,
			});
		}

		if (!editing) {
			this.props.track('FundYourAccount', {
				ScreenName: 'SavingsPlan',
				Action: 'UpdatePlan',
				Amount: amount.value,
				Schedule: frequency.value,
			});
		}

		this.setState({ editing });
	}

	setAmount(amount) {
		this.setState({ amount });
	}

	setFrequency(frequency) {
		this.setState({ frequency });
	}

	renderSavingsCopy() {
		const {
			isRetireAddOn,
			investAccount,
			retireAccount,
			hasInvestEsig,
			hasRetireEsig,
			retireAccountType,
		} = this.props;

		if (isRetireAddOn) {
			const accountTypeCopy =
				(accountTypes[retireAccountType] && `${accountTypes[retireAccountType].name}`) ||
				'retire';

			return (
				<span>
					for my <TileEmphasisText>{accountTypeCopy} account.</TileEmphasisText>
				</span>
			);
		}

		if (hasInvestEsig && hasRetireEsig) {
			return (
				<span>
					split between my
					<div>
						<TileEmphasisText>Personal Investment</TileEmphasisText> and{' '}
						<TileEmphasisText>Roth IRA</TileEmphasisText>
						<Hide.After bp="mobile" display="inline">
							&nbsp;accounts.
						</Hide.After>
					</div>
					<Hide.Until bp="mobile" display="inline">
						accounts.
					</Hide.Until>
				</span>
			);
		}

		const getAccountType = () => {
			const account = retireAccount || investAccount;
			return account.type_title;
		};

		return (
			<span>
				for my <TileEmphasisText>{getAccountType()} account.</TileEmphasisText>
			</span>
		);
	}

	renderEditSavingsCopy() {
		const {
			investAccount,
			retireAccount,
			hasInvestEsig,
			hasRetireEsig,
			retireAccountType,
			isRetireAddOn,
		} = this.props;

		if (isRetireAddOn) {
			const accountTypeCopy =
				(accountTypes[retireAccountType] && `${accountTypes[retireAccountType].name}`) ||
				'Retire';

			return (
				<span>
					for my <TileEmphasisText>{accountTypeCopy} account.</TileEmphasisText>
				</span>
			);
		}

		if (hasInvestEsig && hasRetireEsig) {
			const splitAmount = parseFloat(this.state.amount.value / 2);

			return (
				<span>
					split between my <br />
					<TileEmphasisText>
						Personal Investment (${splitAmount})
					</TileEmphasisText> and{' '}
					<TileEmphasisText>Roth IRA (${splitAmount})</TileEmphasisText> accounts.
				</span>
			);
		}

		const getAccountType = () => {
			if (isRetireAddOn) {
				return (
					accountTypes[retireAccountType] && `${accountTypes[retireAccountType].name}`
				);
			} else {
				const account = retireAccount || investAccount;
				return account.type_title;
			}
		};

		return (
			<span>
				for my <TileEmphasisText>{getAccountType()} account.</TileEmphasisText>
			</span>
		);
	}

	renderSavingsPlan() {
		const { accounts, bankAccount, onSubmit, isRetireAddOn } = this.props;
		const { amount, frequency } = this.state;
		const annualSavings = this.computeSavings(amount.value, frequency.value);

		return (
			<Tile bg="bgSecondary" mt="m">
				<Flex mb="s">
					<Box mr="xs">
						<RecurringIcon size="1em" verticalAlign="middle" />
					</Box>
					<Text.H6>My automatic savings plan</Text.H6>
				</Flex>
				<Box
					css={css`
						line-height: 1.85;
					`}
				>
					I want to set aside
					<div>
						<TileEmphasisText>
							{amount.label} {frequency.label}
						</TileEmphasisText>
					</div>
					{this.renderSavingsCopy()}
					<Box mt="s" mb="m">
						That&apos;s <TileEmphasisText>{annualSavings}</TileEmphasisText> saved a year.
					</Box>
				</Box>
				<PrimaryButton
					type="submit"
					onClick={() => {
						onSubmit(amount.value, frequency.value, accounts, bankAccount, isRetireAddOn);
					}}
				>
					I&apos;m ready
				</PrimaryButton>
				<Flex justifyContent="center">
					<TertiaryButton as={SmartLink} onClick={() => this.setEditing(true)}>
						Edit
					</TertiaryButton>
				</Flex>
			</Tile>
		);
	}

	renderEditSavingsPlan() {
		const { skip, product, isRetireAddOn } = this.props;
		const { amount, frequency } = this.state;
		const annualSavings = this.computeSavings(amount.value, frequency.value);
		const isUniversal = product === 'universal';

		return (
			<>
				<StyledTile overflow="visible">
					<Flex mb="s">
						<Box mr="xs">
							<RecurringIcon size="1em" verticalAlign="middle" />
						</Box>
						<Text.H6>My automatic savings plan</Text.H6>
					</Flex>
					<Text mb="m">I want to set aside:</Text>
					<DropdownContainer className="grid grid-rows-2 mb-contentSmall">
						<Select
							label="Amount"
							value={amount.label}
							onChange={(e) => {
								this.setAmount(e.target);
							}}
							noMessage
						>
							{amounts.map((amount) => (
								<option key={amount} value={amount.value}>
									{amount.label}
								</option>
							))}
						</Select>
						<Select
							label="Frequency"
							value={frequency.label}
							onChange={(e) => {
								this.setFrequency(e.target);
							}}
							noMessage
						>
							{frequencies.map((frequency) => (
								<option key={frequency} value={frequency.value}>
									{frequency.label}
								</option>
							))}
						</Select>
					</DropdownContainer>
					<Text mb="m">{this.renderEditSavingsCopy()}</Text>
					<Text>
						That&apos;s <TileEmphasisText>{annualSavings}</TileEmphasisText> saved a year.
					</Text>
					<div className="signup__secondary">
						<Box mb="l">
							<PrimaryButton
								type="submit"
								onClick={() => {
									this.setEditing(false);
								}}
							>
								Update
							</PrimaryButton>
							<Text mt="s" align="center" color="textTertiary">
								Note: You can always change this later
							</Text>
						</Box>
					</div>
				</StyledTile>
				{!isUniversal && !isRetireAddOn && (
					<Flex mb="m" justifyContent="center">
						<Link as={SmartLink} onClick={skip}>
							Skip for now
						</Link>
					</Flex>
				)}
			</>
		);
	}

	renderSubheader() {
		const { isRetireAddOn } = this.props;
		const autoStashTooltip = (
			<div>
				What is Auto-Stash?
				<br />
				<br />
				Auto-Stash is a customizable tool that helps you save more and invest
				automatically. It is our favorite tool to help you invest automatically. Invest
				once a week or once a month, without having to tax your memory bank. On average,
				users with Auto-Stash turned on save 3x more than users who don’t.
				<br />
				<br />
				How does it work?
				<br />
				<br />
				Choose an amount to deposit into your Stash cash balance and how often you would
				like it to repeat. Auto-Stash will initiate those transfers automatically. You can
				always go back to make changes or turn it off.
				<br />
			</div>
		);

		return (
			<Text mb="m">
				{isRetireAddOn && (
					<span>
						And your future is waiting. Commit.
						<Tooltip label={autoStashTooltip} />
						<br />
					</span>
				)}
				{!isRetireAddOn && (
					<span>
						And your future is waiting. <Tooltip label={autoStashTooltip} />
					</span>
				)}
			</Text>
		);
	}

	render() {
		const { firstName } = this.props;
		const { editing } = this.state;

		return (
			<Layout.PageWrap height="100vh">
				<SignUpStep>
					<Box>
						<Text.H1 size="3" mb="xxs">
							Your plan is ready, {firstName}...
						</Text.H1>
						{this.renderSubheader()}
						<div className="signup__content">
							<TransitionGroup>
								<CSSTransition
									key={`edit-auto-stash-${editing}`}
									classNames="sign-up-content-transition"
									timeout={{ enter: 300, exit: 300 }}
								>
									{editing ? this.renderEditSavingsPlan() : this.renderSavingsPlan()}
								</CSSTransition>
							</TransitionGroup>
						</div>
					</Box>
				</SignUpStep>
			</Layout.PageWrap>
		);
	}
}

export default connect(mapStateToProps, mapDispatchToProps)(SignUpAutoStash);
